import axios from 'axios';

// Use relative API path so it works on both preview and production domains
const BACKEND_URL = window.location.origin || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Request deduplication cache - prevents duplicate in-flight requests
const pendingRequests = new Map();

// Create a deduplicated request wrapper
const deduplicatedRequest = async (key, requestFn) => {
  // If there's already a pending request for this key, return that promise
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key);
  }
  
  // Create and store the new request promise
  const promise = requestFn().finally(() => {
    // Clean up after request completes (success or failure)
    pendingRequests.delete(key);
  });
  
  pendingRequests.set(key, promise);
  return promise;
};

// Create axios instance for authenticated requests
const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include token from localStorage (Safari ITP fix)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle 401 errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear any stale auth state
      localStorage.removeItem('session_token');
      
      // Don't redirect if already on login, auth callback, or public pages
      const currentPath = window.location.pathname;
      const publicPaths = ['/', '/login', '/auth/callback', '/shared', '/privacy', '/terms', '/import', '/landing-v2.html', '/waggle-dance'];
      const isPublicPage = publicPaths.some(path => currentPath.startsWith(path));
      
      if (!isPublicPage) {
        console.warn('Session expired, redirecting to login');
        // Store intended URL before redirect
        if (currentPath !== '/' && currentPath !== '/recipe-box') {
          sessionStorage.setItem('intendedUrl', currentPath + window.location.search);
        }
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Create axios instance for public requests (no credentials)
const publicApi = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth API
export const auth = {
  exchangeSession: (sessionId) =>
    api.get('/auth/session', { headers: { 'X-Session-ID': sessionId } }),
  getCurrentUser: () => api.get('/auth/me'),
  logout: () => {
    localStorage.removeItem('session_token');
    return api.post('/auth/logout');
  },
};

// User API
export const user = {
  getFoodPreferences: () => api.get('/user/food-preferences'),
  updateFoodPreferences: (preferences) => api.put('/user/food-preferences', preferences),
  getMeasurementSystem: () => api.get('/user/measurement-system'),
  updateMeasurementSystem: (measurementSystem) => api.put('/user/measurement-system', { measurement_system: measurementSystem }),
  getCustomSites: () => api.get('/user/custom-sites'),
  addCustomSite: (site) => api.post('/user/custom-sites', site),
  deleteCustomSite: (url) => api.delete('/user/custom-sites', { params: { url } }),
};

// Recipe API
export const recipes = {
  getAll: (params) => api.get('/recipes', { params: { ...params, full: true } }),
  // Lightweight endpoint for ID mapping only - much faster than getAll
  getIdMapping: () => api.get('/recipes/mapping/ids'),
  create: (data) => api.post('/recipes', data),
  // Deduplicated getById - prevents duplicate concurrent requests for same recipe
  getById: (id) => deduplicatedRequest(`recipe:${id}`, () => api.get(`/recipes/${id}`)),
  update: (id, data) => api.put(`/recipes/${id}`, data),
  delete: (id) => api.delete(`/recipes/${id}`),
  restore: (id) => api.post(`/recipes/${id}/restore`),
  markViewed: (id) => api.post(`/recipes/${id}/view`),
  toggleCurrentMeal: (id, setValue) => api.post(`/recipes/${id}/current-meal`, null, { params: setValue !== undefined ? { set_value: setValue } : {} }),
  toggleUpNext: (id, setValue) => api.post(`/recipes/${id}/up-next`, null, { params: setValue !== undefined ? { set_value: setValue } : {} }),
  getThumbnail: (id, size = 400) => api.get(`/recipes/${id}/thumbnail`, { params: { size } }),
  addImage: (id, imageBase64) =>
    api.post(`/recipes/${id}/images`, { image_base64: imageBase64 }),
  deleteImage: (id, imageIndex) =>
    api.delete(`/recipes/${id}/images/${imageIndex}`),
  updateLabels: (id, labels) =>
    api.put(`/recipes/${id}/labels`, { labels }),
  bulkApplyLabel: (labelName) =>
    api.post('/recipes/bulk-labels', { label_name: labelName }),
  share: (id) => api.post(`/recipes/${id}/share`),
  getTweaks: (id) => api.get(`/recipes/${id}/tweaks`),
  enhanceWithAisles: (id) => api.post(`/recipes/${id}/enhance-aisles`, {}, { timeout: 60000 }),
  // Proxy browser functions
  proxyFetch: (url) => api.post('/proxy/fetch', { url }, { timeout: 20000 }),
  checkRecipeExists: (url) => api.get('/proxy/check-recipe', { params: { url } }),
  importFromUrl: (url) => api.post('/ai/import-recipe-url', { url }, { timeout: 30000 }),
  // Paprika import
  importFromPaprika: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/recipes/import-paprika', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000 // 2 min timeout for large files
    });
  },
};

// Shared Recipe API (public, no auth required)
export const shared = {
  getRecipe: (sourceRecipeId) => publicApi.get(`/shared/${sourceRecipeId}`),
  getVariant: (recipeId) => publicApi.get(`/shared/variant/${recipeId}`),
  addToBox: (sourceRecipeId) => api.post(`/shared/${sourceRecipeId}/add`),
  getBuzzHistory: (sourceRecipeId) => publicApi.get(`/shared/${sourceRecipeId}/buzz`),
  getAddCount: (sourceRecipeId) => publicApi.get(`/shared/${sourceRecipeId}/add-count`),
};

// Labels API
export const labels = {
  getAll: () => api.get('/labels'),
  create: (name) => api.post('/labels', { name }),
  delete: (name) => api.delete(`/labels/${encodeURIComponent(name)}`),
};

// AI API
export const ai = {
  generateRecipe: (messages, mode = 'conversation', foodPreferences = null, measurementSystem = null) =>
    api.post('/ai/generate-recipe', { messages, mode, food_preferences: foodPreferences, measurement_system: measurementSystem }),
  analyzeImage: (imageBase64, imageType = null, additionalImages = null, foodPreferences = null, measurementSystem = null) => {
    const payload = { 
      image_base64: imageBase64, 
      additional_images: additionalImages,
      food_preferences: foodPreferences,
      measurement_system: measurementSystem
    };
    // Only include image_type if provided (allows backend auto-detection)
    if (imageType) {
      payload.image_type = imageType;
    }
    return api.post('/ai/analyze-image', payload);
  },
  chat: (recipeId, messages, foodPreferences = null, measurementSystem = null) =>
    api.post('/ai/chat', { recipe_id: recipeId, messages, food_preferences: foodPreferences, measurement_system: measurementSystem }),
  generateMiseEnPlace: (recipeIds, signal = null) =>
    api.post('/ai/mise-en-place', { recipe_ids: recipeIds }, { signal }),
  generateImage: (recipeTitle, recipeDescription) =>
    api.post('/ai/generate-image', { recipe_title: recipeTitle, recipe_description: recipeDescription }, { timeout: 120000 }),
  importRecipeFromURL: (url) =>
    api.post('/ai/import-recipe-url', { url }, { timeout: 30000 }),
  summarizeText: (text, instruction = null) =>
    api.post('/ai/summarize-text', { text, instruction }),
};

// Chats API (conversation history)
export const chats = {
  getAll: () => api.get('/chats'),
  getById: (id) => api.get(`/chats/${id}`),
  getByRecipeId: (recipeId) => api.get(`/chats/recipe/${recipeId}`),
  create: (data) => api.post('/chats', data),
  update: (id, data) => api.put(`/chats/${id}`, data),
  delete: (id) => api.delete(`/chats/${id}`),
};

// Buzz API (cooking journal)
export const buzz = {
  getAll: () => api.get('/buzz'),
  getById: (id) => api.get(`/buzz/${id}`),
  create: (data) => api.post('/buzz', data),
  update: (id, data) => api.put(`/buzz/${id}`, data),
  delete: (id) => api.delete(`/buzz/${id}`),
  // Buzz count for a recipe
  getRecipeCount: (recipeId) => api.get(`/buzz/recipe/${recipeId}/count`),
  // Comments
  getComments: (buzzId) => api.get(`/buzz/${buzzId}/comments`),
  addComment: (buzzId, text) => api.post(`/buzz/${buzzId}/comments`, { text }),
  deleteComment: (buzzId, commentId) => api.delete(`/buzz/${buzzId}/comments/${commentId}`),
};

// Hives API
export const hives = {
  // CRUD
  create: (data) => api.post('/hives', data),
  getById: (id) => api.get(`/hives/${id}`),
  update: (id, data) => api.put(`/hives/${id}`, data),
  delete: (id) => api.delete(`/hives/${id}`),
  
  // Discovery
  getAll: (params) => api.get('/hives', { params }),
  getMyHives: () => api.get('/hives', { params: { my_hives: true } }),
  getPopular: (limit = 10) => api.get('/hives/popular', { params: { limit } }),
  search: (query) => api.get('/hives', { params: { search: query } }),
  getByInviteCode: (inviteCode) => api.get(`/hives/invite/${inviteCode}`),
  getByQueenInviteCode: (queenInviteCode) => api.get(`/hives/queen-invite/${queenInviteCode}`),
  suggestEmoji: (name, description) => api.post('/hives/suggest-emoji', { name, description }),
  
  // Membership
  join: (hiveId, inviteCode = null) => api.post(`/hives/${hiveId}/join`, null, { params: inviteCode ? { invite_code: inviteCode } : {} }),
  joinAsQueen: (hiveId) => api.post(`/hives/${hiveId}/join-as-queen`),
  leave: (hiveId) => api.post(`/hives/${hiveId}/leave`),
  restore: (hiveId) => api.post(`/hives/${hiveId}/restore`),
  getMembers: (hiveId) => api.get(`/hives/${hiveId}/members`),
  markRead: (hiveId) => api.post(`/hives/${hiveId}/mark-read`),
  
  // Queen management (creator only)
  generateQueenInvite: (hiveId) => api.post(`/hives/${hiveId}/generate-queen-invite`),
  getQueens: (hiveId) => api.get(`/hives/${hiveId}/queens`),
  removeQueen: (hiveId, queenUserId) => api.delete(`/hives/${hiveId}/queens/${queenUserId}`),
  
  // Posts
  getPosts: (hiveId, sort = 'recent') => api.get(`/hives/${hiveId}/posts`, { params: { sort } }),
};

// Feed API
export const feed = {
  get: (hiveId = null, sort = 'recent') => api.get('/feed', { params: { hive_id: hiveId, sort } }),
  // Comment functions (delegating to buzz endpoints)
  addComment: (buzzId, text) => api.post(`/buzz/${buzzId}/comments`, { text }),
  getComments: (buzzId) => api.get(`/buzz/${buzzId}/comments`),
  deleteComment: (buzzId, commentId) => api.delete(`/buzz/${buzzId}/comments/${commentId}`),
  // Like functions
  likePost: (buzzId) => api.post(`/buzz/${buzzId}/like`),
  unlikePost: (buzzId) => api.delete(`/buzz/${buzzId}/like`),
};

export const settings = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/admin/settings', data),
};

// Groceries API
export const groceries = {
  // Get active grocery items
  get: () => api.get('/groceries'),
  getAll: () => api.get('/groceries'), // Alias for get()
  
  // Add grocery items (comma-separated or array)
  add: (items) => api.post('/groceries', { items: Array.isArray(items) ? items : [items] }),
  
  // Update single item (e.g., check/uncheck)
  updateItem: (itemId, data) => api.patch(`/groceries/${itemId}`, data),
  // Toggle item checked state (convenience method)
  toggle: (itemId, checked) => api.patch(`/groceries/${itemId}`, { checked }),
  
  // Bulk actions
  checkAll: () => api.patch('/groceries/check-all', {}),
  uncheckAll: () => api.patch('/groceries/uncheck-all', {}),
  reclassify: () => api.post('/groceries/reclassify'),
  
  // Clear items
  clearChecked: () => api.delete('/groceries/checked'),
  clearAll: () => api.delete('/groceries/all'),
  
  // Delete single item
  deleteItem: (itemId) => api.delete(`/groceries/${itemId}`),
  
  // Settings (include_in_shopping checkbox)
  updateSettings: (data) => api.patch('/groceries/settings', data),
  
  // History
  getHistory: () => api.get('/groceries/history'),
  dedupeHistory: () => api.post('/groceries/history/dedupe'),
  addToHistory: (item) => api.post('/groceries/history', item),
  restoreFromHistory: (itemId) => api.post(`/groceries/history/${itemId}/restore`),
  deleteFromHistory: (itemId) => api.delete(`/groceries/history/${itemId}`),
  
  // Hive sync
  getStatus: () => api.get('/groceries/status'),
  linkToHive: (hiveId) => api.post('/groceries/link-to-hive', { hive_id: hiveId }),
  unlinkFromHive: () => api.post('/groceries/unlink-from-hive'),
};

export default api;